import type {
    CashRegisterConfig,
    CashRegisterSimpleConfig,
    CommonConfig,
    DepartmentConfig,
    DeviceConfig,
    MerchantConfig,
    MerchantDepartmentConfig,
    PaymentProviderConfig,
} from "./types";
import type { Schema } from "../schema";
import { merchantDepartmentConfig } from "./default-values";
import { supportedGiftcardVatBookkeepingMethods } from "../../merchant";

// COMMON
export const commonConfigSchema: Schema<CommonConfig> = {
    welcomeMessage: {
        type: "text",
        validation: { required: true, minLength: { length: 3 } },
        section: "common",
    },
    printOrderWhenCompleted: {
        type: "boolean",
        validation: null,
        section: "common",
    },
    logOutWhenCompleted: {
        type: "text_enum",
        validation: null,
        section: "common",
        values: ["yes", "ask", "no"],
    },
    language: {
        type: "select",
        validation: null,
        section: "common",
        values: ["en", "da", "de"],
    },
    buttonLayout: {
        type: "select",
        validation: null,
        section: "common",
    },
    tableLayout: {
        type: "select",
        validation: null,
        section: "common",
    },
    paymentOptions: {
        type: "select",
        validation: null,
        section: "common",
    },
    miscButtons: {
        type: "select",
        validation: null,
        section: "common",
    },
    rotation: {
        type: "select",
        validation: null,
        section: "common",
        values: ["0", "90", "180", "270"],
    },
    autoDeleteOrderIdleTimeout: {
        type: "number",
        validation: null,
        section: "common",
    },
    autoDeleteOrderWarningTimeout: {
        type: "number",
        validation: null,
        section: "common",
    },
    warnOnRefundAndReturn: {
        type: "boolean",
        validation: null,
        section: "common",
    },
    queryCustomersForData: {
        type: "boolean",
        validation: null,
        section: "common",
    },
    enableLayoutSwipe: {
        type: "boolean",
        validation: null,
        section: "common",
    },
    printReceiptNumberOnOrderSummary: {
        type: "boolean",
        validation: null,
        section: "common",
    },
    autoLogoutIdleTimeout: {
        type: "number",
        validation: null,
        section: "common",
    },
    autoLogoutWarningTimeout: {
        type: "number",
        validation: null,
        section: "common",
    },
};

export const paymentProviderConfigSchema: Schema<PaymentProviderConfig> = {
    sumupAffiliateKey: {
        type: "text",
        validation: null,
        section: "paymentProviderSumup",
    },
    sumupClientId: {
        type: "text",
        validation: null,
        section: "paymentProviderSumup",
    },
    sumupClientSecret: {
        type: "text",
        validation: null,
        section: "paymentProviderSumup",
    },
    sumupRefreshToken: {
        type: "text",
        validation: null,
        section: "paymentProviderSumup",
    },
    sumupRedirectUrl: {
        type: "text",
        validation: null,
        section: "paymentProviderSumup",
    },
    softpayIntegratorId: {
        type: "text",
        validation: null,
        section: "paymentProviderSoftpay",
    },
    softpayMerchantReference: {
        type: "text",
        validation: null,
        section: "paymentProviderSoftpay",
    },
    adyenTerminalApiKey: {
        type: "text",
        validation: null,
        section: "paymentProviderAdyen",
        superadminOnly: true,
    },
    adyenAccountHolderId: {
        type: "text",
        validation: null,
        section: "paymentProviderAdyen",
        superadminOnly: true,
    },
    adyenStoreId: {
        type: "text",
        validation: null,
        section: "paymentProviderAdyen",
        superadminOnly: true,
    },
    adyenKeyPassphrase: {
        type: "text",
        validation: null,
        section: "paymentProviderAdyen",
    },
    adyenKeyIdentifier: {
        type: "text",
        validation: null,
        section: "paymentProviderAdyen",
    },
    adyenKeyVersion: {
        type: "number",
        validation: null,
        section: "paymentProviderAdyen",
    },
    adyenCryptoVersion: {
        type: "number",
        validation: null,
        section: "paymentProviderAdyen",
    },
};

// MERCHANT & DEPARTMENT
export const merchantDepartmentConfigSchema: Schema<MerchantDepartmentConfig> =
    {
        address: {
            type: "text",
            validation: null,
            section: "location",
        },
        zipcode: {
            type: "text",
            validation: null,
            section: "location",
        },
        city: {
            type: "text",
            validation: null,
            section: "location",
        },
        country: {
            type: "text",
            validation: null,
            section: "location",
        },
        email: {
            type: "text",
            validation: null,
            section: "location",
        },
        phone: {
            type: "text",
            validation: null,
            section: "location",
        },
        cvr: {
            type: "text",
            validation: null,
            section: "location",
        },
        headerText: {
            type: "text",
            validation: null,
            section: "receipt",
        },
        footerText: {
            type: "text",
            validation: null,
            section: "receipt",
        },
        showServedBy: {
            type: "boolean",
            validation: null,
            section: "receipt",
        },
        // showLogoOnReceipt: {
        //     type: "boolean",
        //     validation: null,
        //     section: "receipt",
        // },

        ...paymentProviderConfigSchema,
    };

// MERCHANT
export const merchantConfigSchema: Schema<MerchantConfig> = {
    ...commonConfigSchema,
    ...merchantDepartmentConfigSchema,
    defaultVat: {
        type: "select",
        validation: {
            required: true,
        },
        section: "merchant",
    },
    defaultDepartment: {
        type: "select",
        validation: null,
        section: "merchant",
    },
    currency: {
        locked: true,
        type: "text",
        validation: {
            required: true,
            minLength: { length: 3 },
            maxLength: { length: 3 },
        },
        section: "merchant",
    },
    giftcardVatBookkeepingMethod: {
        locked: true,
        type: "text_enum",
        validation: {
            required: true,
        },
        section: "merchant",
        values: supportedGiftcardVatBookkeepingMethods,
    },
    barcodeOnReceipt: {
        type: "boolean",
        validation: null,
        section: "receipt",
    },

    showDestinationOnShift: {
        type: "boolean",
        validation: null,
        section: "shift",
    },
    headerOnPaymentReceipt: {
        type: "boolean",
        validation: null,
        section: "receipt",
    },
};

// DEPARTMENT
export const departmentConfigSchema: Schema<DepartmentConfig> = {
    ...commonConfigSchema,
    ...merchantDepartmentConfigSchema,
};

// CASH REGISTER
export const cashRegisterConfigSchema: Schema<CashRegisterConfig> = {
    ...commonConfigSchema,
    receiptPrinter: {
        type: "select",
        validation: null,
        section: "receipt",
    },
    ticketPrinter: {
        type: "select",
        validation: null,
        section: "receipt",
    },
    showServedBy: {
        type: "boolean",
        validation: null,
        section: "receipt",
    },
    // showLogoOnReceipt: {
    //     type: "boolean",
    //     validation: null,
    //     section: "receipt",
    // },

    startShiftCashAmount: {
        type: "number",
        validation: { minValue: { value: 0 } },
        section: "shift",
    },
    startShiftWithLastCashAmount: {
        type: "boolean",
        validation: null,
        section: "shift",
    },
};

// CASH REGISTER SIMPLE CONFIG for the Multi Edit screen.
export const cashRegisterSimpleConfigSchema: Schema<CashRegisterSimpleConfig> =
    {
        softpayCloudTerminalAppId: {
            type: "select",
            validation: null,
            section: "paymentProvider",
        },
        adyenTerminalApiPoiId: {
            // Notice how this is a text input for easy input on the Multi Edit screen
            // - compared to a select/dropdown on the single edit screen.
            type: "text",
            validation: { required: true, minLength: { length: 3 } },
            section: "paymentProvider",
        },
    };

// CASH REGISTER PAYMENT PROVIDER CONFIG for the Edit Cash Register screen.
export const cashRegisterPaymentProviderConfigSchema: Schema<CashRegisterSimpleConfig> =
    {
        softpayCloudTerminalAppId: {
            type: "select",
            validation: null,
            section: "paymentProvider",
        },
        adyenTerminalApiPoiId: {
            type: "select",
            validation: { required: true, minLength: { length: 3 } },
            section: "paymentProvider",
            allowCreateInSelect: true,
        },
    };

export const deviceConfigSchema: Schema<DeviceConfig> = {
    ...merchantConfigSchema,
    ...merchantDepartmentConfig,
    ...departmentConfigSchema,
    ...cashRegisterConfigSchema,
    ...cashRegisterPaymentProviderConfigSchema,
};
